/**
 * Simulation Store
 * Global state management for the simulation
 * Supports multiplayer with session and player management
 */

import { create } from 'zustand';
import type {
  FoodData,
  WorldStats,
  SimulationStatus,
  CreatureDetails,
  BrainData,
  UserSession,
  GuidanceSettings,
  PlayerInfo,
  Leaderboard,
  MyCreatureState,
  CreatureDataWithOwner,
} from '../types/simulation';

interface SimulationState {
  // Connection
  isConnected: boolean;

  // Session (multiplayer)
  sessionId: string | null;
  displayName: string | null;
  isAdmin: boolean;

  // My creature
  myCreatureId: string | null;
  myCreatureAlive: boolean;
  guidance: GuidanceSettings;
  waypoint: { x: number; y: number } | null;

  // Players
  players: PlayerInfo[];
  leaderboard: Leaderboard;
  connectedCount: number;

  // Simulation status
  simulationState: 'stopped' | 'running' | 'paused';
  speed: number;
  tick: number;
  actualTPS: number;

  // World data
  creatures: CreatureDataWithOwner[];
  food: FoodData[];
  stats: WorldStats;

  // Selection
  selectedCreatureId: string | null;
  selectedCreature: CreatureDetails | null;
  selectedCreatureBrain: BrainData | null;

  // Camera
  cameraX: number;
  cameraY: number;
  cameraZoom: number;
  followCreature: boolean;

  // Actions
  setConnected: (connected: boolean) => void;
  setSession: (session: UserSession) => void;
  setMyCreature: (state: MyCreatureState) => void;
  setGuidance: (guidance: GuidanceSettings) => void;
  setWaypoint: (point: { x: number; y: number } | null) => void;
  setPlayers: (players: PlayerInfo[], leaderboard: Leaderboard, connectedCount: number) => void;
  setSimulationStatus: (status: SimulationStatus) => void;
  setWorldState: (creatures: CreatureDataWithOwner[], food: FoodData[], stats: WorldStats, tick: number) => void;
  setSelectedCreature: (id: string | null) => void;
  setCreatureDetails: (details: CreatureDetails | null) => void;
  setCreatureBrain: (brain: BrainData | null) => void;
  setCamera: (x: number, y: number, zoom?: number) => void;
  setCameraZoom: (zoom: number) => void;
  setFollowCreature: (follow: boolean) => void;
  reset: () => void;
}

const initialStats: WorldStats = {
  population: 0,
  food: 0,
  generation: 0,
  species: 0,
};

const initialGuidance: GuidanceSettings = {
  thrustBias: 0,
  aggression: 0,
  exploration: 0,
  reproduction: 0,
};

const initialLeaderboard: Leaderboard = {
  oldest: [],
  mostOffspring: [],
  highestEnergy: [],
};

export const useSimulationStore = create<SimulationState>((set) => ({
  // Initial state
  isConnected: false,

  // Session
  sessionId: null,
  displayName: null,
  isAdmin: false,

  // My creature
  myCreatureId: null,
  myCreatureAlive: false,
  guidance: initialGuidance,
  waypoint: null,

  // Players
  players: [],
  leaderboard: initialLeaderboard,
  connectedCount: 0,

  // Simulation
  simulationState: 'stopped',
  speed: 1,
  tick: 0,
  actualTPS: 0,
  creatures: [],
  food: [],
  stats: initialStats,

  // Selection
  selectedCreatureId: null,
  selectedCreature: null,
  selectedCreatureBrain: null,

  // Camera
  cameraX: 0,
  cameraY: 0,
  cameraZoom: 1,
  followCreature: false,

  // Actions
  setConnected: (connected) => set({ isConnected: connected }),

  setSession: (session) =>
    set({
      sessionId: session.sessionId,
      displayName: session.displayName,
      isAdmin: session.isAdmin,
      myCreatureId: session.creatureId,
      myCreatureAlive: session.creatureId !== null,
      guidance: session.guidance,
    }),

  setMyCreature: (state) =>
    set((prev) => {
      const updates: Partial<SimulationState> = {
        myCreatureId: state.id,
        myCreatureAlive: state.alive,
      };

      // Reset guidance and waypoint when creature dies
      if (!state.alive) {
        updates.guidance = initialGuidance;
        updates.waypoint = null;
      }

      // If creature data is provided, add it to creatures array immediately
      // This prevents the "Cargando..." state while waiting for world:state
      if (state.creature && state.alive) {
        const existingIndex = prev.creatures.findIndex((c) => c.id === state.creature!.id);
        if (existingIndex === -1) {
          // Add new creature
          updates.creatures = [...prev.creatures, state.creature];
        } else {
          // Update existing (shouldn't happen normally but handle gracefully)
          updates.creatures = prev.creatures.map((c, i) =>
            i === existingIndex ? state.creature! : c
          );
        }
      }

      return updates;
    }),

  setGuidance: (guidance) => set({ guidance }),

  setWaypoint: (point) => set({ waypoint: point }),

  setPlayers: (players, leaderboard, connectedCount) =>
    set({ players, leaderboard, connectedCount }),

  setSimulationStatus: (status) =>
    set({
      simulationState: status.state,
      speed: status.speed,
      tick: status.tick,
      actualTPS: status.actualTPS,
    }),

  setWorldState: (creatures, food, stats, tick) =>
    set({ creatures, food, stats, tick }),

  setSelectedCreature: (id) =>
    set({
      selectedCreatureId: id,
      selectedCreature: null,
      selectedCreatureBrain: null,
    }),

  setCreatureDetails: (details) => set({ selectedCreature: details }),

  setCreatureBrain: (brain) => set({ selectedCreatureBrain: brain }),

  setCamera: (x, y, zoom) =>
    set((state) => ({
      cameraX: x,
      cameraY: y,
      cameraZoom: zoom ?? state.cameraZoom,
    })),

  setCameraZoom: (zoom) => set({ cameraZoom: Math.max(0.1, Math.min(5, zoom)) }),

  setFollowCreature: (follow) => set({ followCreature: follow }),

  reset: () =>
    set({
      simulationState: 'stopped',
      tick: 0,
      creatures: [],
      food: [],
      stats: initialStats,
      selectedCreatureId: null,
      selectedCreature: null,
      selectedCreatureBrain: null,
      myCreatureId: null,
      myCreatureAlive: false,
      guidance: initialGuidance,
      waypoint: null,
    }),
}));

// Selectors
export const selectCreatureById = (id: string) => (state: SimulationState) =>
  state.creatures.find((c) => c.id === id);

export const selectIsRunning = (state: SimulationState) =>
  state.simulationState === 'running';

export const selectIsPaused = (state: SimulationState) =>
  state.simulationState === 'paused';

export const selectMyCreature = (state: SimulationState) =>
  state.myCreatureId ? state.creatures.find((c) => c.id === state.myCreatureId) : null;

export const selectCanSpawn = (state: SimulationState) =>
  !state.myCreatureAlive;

export const selectIsMyCreature = (creatureId: string) => (state: SimulationState) =>
  state.myCreatureId === creatureId;
